import { Modal as MantineModal } from '@mantine/core';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';

import { Button } from '../Button';
import { CloseButton } from '../CloseButton';
import { ThemeIcon } from '../ThemeIcon';
import styles from './ModalMessage.module.css';

export type ModalMessageVariant = 'negative' | 'regular' | 'positive';

export interface ModalMessageProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback fired when the modal is closed (overlay click, escape, close button) */
  onClose: () => void;
  /** Visual variant — controls ThemeIcon color and primary button style */
  variant?: ModalMessageVariant;
  /** Modal title text */
  title: string;
  /** Primary body text */
  description?: string;
  /** Secondary (dimmed) body text shown below description */
  secondaryText?: string;
  /** Label for the primary CTA button */
  primaryLabel: string;
  /** Callback fired when the primary CTA is clicked */
  onPrimaryAction?: () => void;
  /** Label for the secondary (Tertiary) CTA button — omit to hide */
  secondaryLabel?: string;
  /** Callback fired when the secondary CTA is clicked */
  onSecondaryAction?: () => void;
  /** Whether to show the ThemeIcon above the title */
  withIcon?: boolean;
  /** Whether to show the close button (set false when an explicit action is required) */
  withCloseButton?: boolean;
  /** Whether the modal is centered vertically */
  isCentered?: boolean;
  /** Modal content width */
  size?: string | number;
  /** Whether to close the modal when clicking outside */
  shouldCloseOnClickOutside?: boolean;
  /** Whether to close the modal when pressing Escape */
  shouldCloseOnEscape?: boolean;
  /** Override the default z-index (use when stacking above another modal) */
  zIndex?: number;
  className?: string;
}

const THEME_ICON_COLOR = {
  negative: 'red',
  regular: 'indigo',
  positive: 'green',
} as const;

const THEME_ICON_COMPONENT = {
  negative: IconAlertCircle,
  regular: IconAlertCircle,
  positive: IconCheck,
} as const;

const MODAL_CLASS_NAMES = {
  content: styles.modalContent,
  body: styles.modalBody,
};

export function ModalMessage({
  isOpen,
  onClose,
  variant = 'regular',
  title,
  description,
  secondaryText,
  primaryLabel,
  onPrimaryAction,
  secondaryLabel,
  onSecondaryAction,
  withIcon = true,
  withCloseButton = true,
  isCentered = true,
  size = 480,
  shouldCloseOnClickOutside,
  shouldCloseOnEscape,
  zIndex,
  className,
}: ModalMessageProps) {
  const IconComponent = THEME_ICON_COMPONENT[variant];
  const iconColor = THEME_ICON_COLOR[variant];
  const primaryVariant = variant === 'negative' ? 'negative' : 'primary';

  return (
    <MantineModal
      opened={isOpen}
      onClose={onClose}
      withCloseButton={false}
      centered={isCentered}
      size={size}
      closeOnClickOutside={shouldCloseOnClickOutside}
      closeOnEscape={shouldCloseOnEscape}
      zIndex={zIndex}
      classNames={MODAL_CLASS_NAMES}
    >
      <div className={`${styles.root} ${className ?? ''}`}>
        <div className={styles.headerRow}>
          <div className={styles.contentContainer}>
            {withIcon && (
              <ThemeIcon color={iconColor} size="32">
                <IconComponent size={16} stroke={2} />
              </ThemeIcon>
            )}
            <div className={styles.titleContainer}>
              <p className={styles.title}>{title}</p>
              {description && (
                <div className={styles.bodyContainer}>
                  <p className={styles.bodyPrimary}>{description}</p>
                  {secondaryText && (
                    <p className={styles.bodySecondary}>{secondaryText}</p>
                  )}
                </div>
              )}
            </div>
          </div>
          {withCloseButton && (
            <CloseButton onClick={onClose} aria-label="Close" />
          )}
        </div>
        <div className={styles.footerContainer}>
          <div className={styles.buttonsRow}>
            {secondaryLabel && (
              <Button variant="tertiary" onClick={onSecondaryAction}>
                {secondaryLabel}
              </Button>
            )}
            <Button variant={primaryVariant} onClick={onPrimaryAction}>
              {primaryLabel}
            </Button>
          </div>
        </div>
      </div>
    </MantineModal>
  );
}

ModalMessage.displayName = 'ModalMessage';
